import React, { useState, useEffect } from 'react';
import AdminNav from '../components/AdminNav';
import {
  importVolunteersFromCSV,
  getLocations, addLocation, updateLocation, deleteLocation,
  getCongregations, addCongregation, updateCongregation, deleteCongregation,
  getShiftTimes, addShiftTime, updateShiftTime, deleteShiftTime, resetShiftTimes, migrateAvailabilityValues,
  getMessageTemplates, saveMessageTemplate, DEFAULT_MESSAGE_TEMPLATES,
  seedDefaultData
} from '../services/firebase';
import './Admin.css';

// Format "HH:MM" (24h time input value) → "H:MM AM/PM"
function formatTime12(t) {
  if (!t) return '';
  const [h, m] = t.split(':').map(Number);
  const period = h >= 12 ? 'PM' : 'AM';
  const h12 = h % 12 || 12;
  return `${h12}:${String(m).padStart(2, '0')} ${period}`;
}

function CSVImport({ onLogout }) {
  const [activeTab, setActiveTab] = useState('csv');

  // CSV Import state
  const [file, setFile] = useState(null);
  const [volunteerType, setVolunteerType] = useState('local');
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState(null);

  // Locations state
  const [locations, setLocations] = useState([]);
  const [newLocationName, setNewLocationName] = useState('');
  const [locationLoading, setLocationLoading] = useState(false);
  const [editingLocationId, setEditingLocationId] = useState(null);
  const [editingLocationName, setEditingLocationName] = useState('');
  const [editingLocationAddress, setEditingLocationAddress] = useState('');

  // Congregations state
  const [congregations, setCongregations] = useState([]);
  const [newCongregationName, setNewCongregationName] = useState('');
  const [congregationLoading, setCongregationLoading] = useState(false);
  const [expandedCong, setExpandedCong] = useState(null); // id of congregation showing meeting time editor
  const [meetingEdits, setMeetingEdits] = useState({}); // { [id]: { midweekDay, midweekTime, weekendDay, weekendTime } }

  // Shift Times state
  const [shiftTimes, setShiftTimes] = useState([]);
  const [newShiftName, setNewShiftName] = useState('');
  const [newShiftOrder, setNewShiftOrder] = useState('');
  const [shiftLoading, setShiftLoading] = useState(false);
  const [resettingShifts, setResettingShifts] = useState(false);
  const [resetShiftResult, setResetShiftResult] = useState(null);
  const [migratingAvail, setMigratingAvail] = useState(false);
  const [migrateAvailResult, setMigrateAvailResult] = useState(null);

  // Seed state
  const [seeding, setSeeding] = useState(false);
  const [seedResult, setSeedResult] = useState(null);

  // Message Templates state
  const [templateEdits, setTemplateEdits] = useState({}); // id -> edited field values
  const [templateSaving, setTemplateSaving] = useState({}); // id -> bool
  const [templateSaved, setTemplateSaved] = useState({});   // id -> bool (flash)
  const [templatesLoaded, setTemplatesLoaded] = useState(false);

  useEffect(() => {
    if (activeTab === 'locations') loadLocations();
    if (activeTab === 'congregations') loadCongregations();
    if (activeTab === 'shifttimes') loadShiftTimes();
    if (activeTab === 'messages') loadTemplates();
  }, [activeTab]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadTemplates = async () => {
    const docs = await getMessageTemplates();
    const map = {};
    docs.forEach(d => { map[d.id] = d; });
    // Initialise edits from loaded data (or defaults for any missing)
    const edits = {};
    for (const id of Object.keys(DEFAULT_MESSAGE_TEMPLATES)) {
      edits[id] = { ...DEFAULT_MESSAGE_TEMPLATES[id], ...(map[id] || {}) };
    }
    setTemplateEdits(edits);
    setTemplatesLoaded(true);
  };

  const handleTemplateField = (id, field, value) => {
    setTemplateEdits(prev => ({ ...prev, [id]: { ...prev[id], [field]: value } }));
    setTemplateSaved(prev => ({ ...prev, [id]: false }));
  };

  const handleSaveTemplate = async (id) => {
    setTemplateSaving(prev => ({ ...prev, [id]: true }));
    const data = templateEdits[id];
    await saveMessageTemplate(id, data);
    setTemplateSaving(prev => ({ ...prev, [id]: false }));
    setTemplateSaved(prev => ({ ...prev, [id]: true }));
    setTimeout(() => setTemplateSaved(prev => ({ ...prev, [id]: false })), 3000);
  };

  const handleResetTemplate = (id) => {
    const def = DEFAULT_MESSAGE_TEMPLATES[id];
    setTemplateEdits(prev => ({ ...prev, [id]: { ...def } }));
    setTemplateSaved(prev => ({ ...prev, [id]: false }));
  };

  const handleSeedTemplates = async () => {
    for (const [id, data] of Object.entries(DEFAULT_MESSAGE_TEMPLATES)) {
      await saveMessageTemplate(id, data);
    }
    await loadTemplates();
  };

  const loadShiftTimes = async () => {
    setShiftLoading(true);
    const data = await getShiftTimes();
    setShiftTimes(data);
    setShiftLoading(false);
  };

  const loadLocations = async () => {
    setLocationLoading(true);
    const data = await getLocations();
    setLocations(data);
    setLocationLoading(false);
  };

  const loadCongregations = async () => {
    setCongregationLoading(true);
    const data = await getCongregations();
    setCongregations(data);
    setCongregationLoading(false);
  };

  // --- Shift Time handlers ---
  const handleAddShiftTime = async () => {
    if (!newShiftName.trim()) return;
    const order = parseInt(newShiftOrder, 10) || (shiftTimes.length + 1);
    await addShiftTime(newShiftName.trim(), order);
    setNewShiftName('');
    setNewShiftOrder('');
    loadShiftTimes();
  };

  const handleToggleShiftTime = async (st) => {
    await updateShiftTime(st.id, { active: !st.active });
    loadShiftTimes();
  };

  const handleDeleteShiftTime = async (id) => {
    const confirmed = window.prompt('Type "DELETE" to confirm deleting this shift time:');
    if (confirmed !== 'DELETE') { if (confirmed !== null) alert('Confirmation text did not match. No changes made.'); return; }
    await deleteShiftTime(id);
    loadShiftTimes();
  };

  const handleResetShiftTimes = async () => {
    const confirmed = window.prompt('This will DELETE all current shift times and replace them with the 4 new defaults (7-10 AM, 10 AM-1 PM, 1-5 PM, 5-8 PM). Type "RESET" to confirm:');
    if (confirmed !== 'RESET') { if (confirmed !== null) alert('Confirmation text did not match. No changes made.'); return; }
    setResettingShifts(true);
    setResetShiftResult(null);
    const result = await resetShiftTimes();
    setResetShiftResult(result);
    setResettingShifts(false);
    loadShiftTimes();
  };

  const handleMigrateAvailability = async () => {
    if (!window.confirm('This will update volunteers who have specific shift selections to use the new shift time names. Volunteers marked "Anytime" or "Not Available" are not changed. Continue?')) return;
    setMigratingAvail(true);
    setMigrateAvailResult(null);
    const result = await migrateAvailabilityValues();
    setMigrateAvailResult(result);
    setMigratingAvail(false);
  };

  // --- Location handlers ---
  const handleAddLocation = async () => {
    if (!newLocationName.trim()) return;
    await addLocation({ name: newLocationName.trim() });
    setNewLocationName('');
    loadLocations();
  };

  const handleToggleLocation = async (loc) => {
    await updateLocation(loc.id, { active: !loc.active });
    loadLocations();
  };

  const handleDeleteLocation = async (id) => {
    const confirmed = window.prompt('Type "DELETE" to confirm deleting this location:');
    if (confirmed !== 'DELETE') { if (confirmed !== null) alert('Confirmation text did not match. No changes made.'); return; }
    await deleteLocation(id);
    loadLocations();
  };

  const handleStartEditLocation = (loc) => {
    setEditingLocationId(loc.id);
    setEditingLocationName(loc.name);
    setEditingLocationAddress(loc.address || '');
  };

  const handleSaveLocationName = async () => {
    if (!editingLocationName.trim() || !editingLocationId) return;
    await updateLocation(editingLocationId, { name: editingLocationName.trim(), address: editingLocationAddress.trim() });
    setEditingLocationId(null);
    setEditingLocationName('');
    setEditingLocationAddress('');
    loadLocations();
  };

  const handleCancelEditLocation = () => {
    setEditingLocationId(null);
    setEditingLocationName('');
    setEditingLocationAddress('');
  };

  // --- Congregation handlers ---
  const handleAddCongregation = async () => {
    if (!newCongregationName.trim()) return;
    await addCongregation(newCongregationName.trim());
    setNewCongregationName('');
    loadCongregations();
  };

  const handleToggleCongregation = async (cong) => {
    await updateCongregation(cong.id, { active: !cong.active });
    loadCongregations();
  };

  const handleDeleteCongregation = async (id) => {
    const confirmed = window.prompt('Type "DELETE" to confirm deleting this congregation:');
    if (confirmed !== 'DELETE') { if (confirmed !== null) alert('Confirmation text did not match. No changes made.'); return; }
    await deleteCongregation(id);
    loadCongregations();
  };

  const toggleCongExpand = (cong) => {
    if (expandedCong === cong.id) {
      setExpandedCong(null);
    } else {
      setExpandedCong(cong.id);
      setMeetingEdits(prev => ({
        ...prev,
        [cong.id]: {
          midweekDay: cong.midweekMeetingDay || '',
          midweekTime: cong.midweekMeetingTime || '',
          weekendDay: cong.weekendMeetingDay || '',
          weekendTime: cong.weekendMeetingTime || '',
        },
      }));
    }
  };

  const handleSaveMeetingTimes = async (id) => {
    const edits = meetingEdits[id] || {};
    await updateCongregation(id, {
      midweekMeetingDay: edits.midweekDay || '',
      midweekMeetingTime: edits.midweekTime || '',
      weekendMeetingDay: edits.weekendDay || '',
      weekendMeetingTime: edits.weekendTime || '',
    });
    setExpandedCong(null);
    loadCongregations();
  };

  const handleSeed = async () => {
    const password = window.prompt('Enter setup password to load default data:');
    if (password !== 'uhhh-dozen8REQUIRE') {
      if (password !== null) alert('Incorrect password.');
      return;
    }
    if (!window.confirm('This will add all default locations and congregations to empty collections. Existing data will not be changed. Continue?')) return;
    setSeeding(true);
    setSeedResult(null);
    const result = await seedDefaultData();
    setSeedResult(result);
    setSeeding(false);
    if (result.success) {
      if (activeTab === 'locations') loadLocations();
      if (activeTab === 'congregations') loadCongregations();
    }
  };

  // --- CSV handlers ---
  const handleFileChange = (e) => {
    setFile(e.target.files[0]);
    setResult(null);
  };

  // Parse a single CSV line respecting quoted fields (fields may contain commas)
  const parseCSVLine = (line) => {
    const values = [];
    let current = '';
    let inQuotes = false;
    for (let i = 0; i < line.length; i++) {
      const ch = line[i];
      if (ch === '"') {
        if (inQuotes && line[i + 1] === '"') { current += '"'; i++; }
        else inQuotes = !inQuotes;
      } else if (ch === ',' && !inQuotes) {
        values.push(current.trim());
        current = '';
      } else {
        current += ch;
      }
    }
    values.push(current.trim());
    return values;
  };

  // Parse availability value: 'Anytime' | 'Not Available' | semicolon-list of shifts → array
  const parseAvailability = (raw, defaultVal) => {
    const v = (raw || '').trim();
    if (!v) return defaultVal;
    if (v === 'Anytime' || v === 'Not Available') return v;
    // Semicolon-separated shift list: "7-9 AM;9-11 AM"
    if (v.includes(';')) return v.split(';').map(s => s.trim()).filter(Boolean);
    // Single shift time
    return [v];
  };

  const parseCSV = (text) => {
    const lines = text.split('\n').filter(line => line.trim());
    const headers = parseCSVLine(lines[0]);

    const volunteers = [];
    for (let i = 1; i < lines.length; i++) {
      const values = parseCSVLine(lines[i]);
      const defaultAvail = ''; // empty = not yet provided; only set 'Anytime'/'Not Available' if CSV cell says so
      const volunteer = { volunteerType };

      headers.forEach((header, index) => {
        const h = header.toLowerCase();
        const v = values[index] ?? '';
        if (h.includes('first') && h.includes('name')) {
          volunteer.firstName = v;
        } else if (h.includes('last') && h.includes('name')) {
          volunteer.lastName = v;
        } else if (h.includes('email')) {
          volunteer.email = v;
        } else if (h.includes('phone')) {
          volunteer.phone = v;
        } else if (h.includes('congregation')) {
          volunteer.congregation = v;
        } else if (h.includes('keyman') || h.includes('key man')) {
          const up = v.toUpperCase();
          volunteer.isKeyman = up === 'YES' || up === 'TRUE' || up === '1' || up === 'Y';
        } else if (h.includes('thu')) {
          volunteer.thuAvailability = parseAvailability(v, defaultAvail);
        } else if (h.includes('fri')) {
          volunteer.friAvailability = parseAvailability(v, defaultAvail);
        } else if (h.includes('sat')) {
          volunteer.satAvailability = parseAvailability(v, defaultAvail);
        } else if (h.includes('sun')) {
          volunteer.sunAvailability = parseAvailability(v, defaultAvail);
        } else if (h.includes('note')) {
          volunteer.notes = v;
        }
      });

      if (volunteer.firstName && volunteer.lastName) {
        volunteers.push(volunteer);
      }
    }
    return volunteers;
  };

  const handleImport = async () => {
    if (!file) {
      alert('Please select a CSV file');
      return;
    }
    setImporting(true);
    setResult(null);
    try {
      const text = await file.text();
      const volunteers = parseCSV(text);
      if (volunteers.length === 0) {
        setResult({ success: false, error: 'No valid volunteers found in CSV' });
        setImporting(false);
        return;
      }
      const importResult = await importVolunteersFromCSV(volunteers);
      setResult(importResult);
      if (importResult.success) setFile(null);
    } catch (error) {
      setResult({ success: false, error: error.message });
    } finally {
      setImporting(false);
    }
  };

  return (
    <div className="admin-container">
      <AdminNav onLogout={onLogout} />
      <header className="admin-header">
        <h1>Settings</h1>
        <p className="page-subtitle">Import volunteers from CSV, manage congregation and location lists, and load default data.</p>
      </header>

      <div className="settings-content">
        <div className="seed-banner">
          <div className="seed-banner-text">
            <strong>First time setup?</strong> Load the default locations and congregations into Firestore.
            Collections that already have data will not be overwritten.
          </div>
          <button onClick={handleSeed} disabled={seeding} className="btn btn-secondary">
            {seeding ? 'Loading...' : 'Load Default Data'}
          </button>
          {seedResult && (
            <span className={`seed-result ${seedResult.success ? 'success' : 'error'}`}>
              {seedResult.success
                ? `Done — added ${seedResult.congregations} congregation(s), ${seedResult.locations} location(s), ${seedResult.shiftTimes || 0} shift time(s).${seedResult.skipped.length ? ` Skipped (not empty): ${seedResult.skipped.join(', ')}.` : ''}`
                : `Error: ${seedResult.error}`
              }
            </span>
          )}
        </div>

        <div className="settings-tabs">
          <button
            className={activeTab === 'csv' ? 'active' : ''}
            onClick={() => setActiveTab('csv')}
          >
            CSV Import
          </button>
          <button
            className={activeTab === 'locations' ? 'active' : ''}
            onClick={() => setActiveTab('locations')}
          >
            Locations
          </button>
          <button
            className={activeTab === 'congregations' ? 'active' : ''}
            onClick={() => setActiveTab('congregations')}
          >
            Congregations
          </button>
          <button
            className={activeTab === 'shifttimes' ? 'active' : ''}
            onClick={() => setActiveTab('shifttimes')}
          >
            Shift Times
          </button>
          <button
            className={activeTab === 'messages' ? 'active' : ''}
            onClick={() => setActiveTab('messages')}
          >
            Messages
          </button>
        </div>

        {/* CSV Import Tab */}
        {activeTab === 'csv' && (
          <div className="import-section">
            <h2>Import Volunteers from CSV</h2>

            <div className="form-group">
              <label>Volunteer Type:</label>
              <select value={volunteerType} onChange={(e) => setVolunteerType(e.target.value)}>
                <option value="local">Local Volunteers</option>
                <option value="smpw">SMPW Volunteers</option>
              </select>
            </div>

            <div className="form-group">
              <label>Select CSV File:</label>
              <input type="file" accept=".csv" onChange={handleFileChange} />
            </div>

            <div className="csv-format-guide">
              <h3>CSV Format Requirements:</h3>
              <p>Your CSV should have these columns (order doesn't matter):</p>
              <ul>
                <li><strong>First Name</strong> — Required</li>
                <li><strong>Last Name</strong> — Required</li>
                <li><strong>Email</strong> — Optional</li>
                <li><strong>Phone</strong> — Optional</li>
                <li><strong>Congregation</strong> — Optional</li>
                <li><strong>Keyman</strong> — Optional (YES/NO, SMPW only)</li>
                <li><strong>Notes</strong> — Optional internal notes</li>
                <li><strong>Thu / Fri / Sat / Sun</strong> — Availability: <code>Anytime</code>, <code>Not Available</code>, or semicolon-separated shift times (e.g. <code>7-9 AM;9-11 AM</code>)</li>
              </ul>

              <details>
                <summary>Example CSV for Local Volunteers</summary>
                <pre>
First Name,Last Name,Email,Phone,Congregation,Thu,Fri,Sat,Sun,Notes
John,Smith,john@example.com,706-555-1234,Evans,"7-9 AM;9-11 AM",Anytime,Not Available,Not Available,Prefers morning
Jane,Doe,,706-555-5678,Augusta,Anytime,Anytime,Anytime,Not Available,
                </pre>
              </details>

              <details>
                <summary>Example CSV for SMPW Volunteers</summary>
                <pre>
First Name,Last Name,Phone,Keyman,Notes
John,Doe,706-555-1234,YES,
Jane,Smith,,NO,Arriving Thursday evening
                </pre>
                <p><small>For SMPW volunteers, availability defaults to "Anytime" if not specified.</small></p>
              </details>
            </div>

            <button
              onClick={handleImport}
              disabled={!file || importing}
              className="btn btn-primary"
            >
              {importing ? 'Importing...' : 'Import Volunteers'}
            </button>

            {result && (
              <div className={`result-message ${result.success ? 'success' : 'error'}`}>
                {result.success
                  ? <>Import complete: {result.added} added, {result.updated} updated{result.errors?.length > 0 ? ` (${result.errors.length} error${result.errors.length !== 1 ? 's' : ''})` : ''}.</>
                  : <>Import failed: {result.error}</>
                }
              </div>
            )}
          </div>
        )}

        {/* Locations Tab */}
        {activeTab === 'locations' && (
          <div className="management-section">
            <h2>Manage Cart Witnessing Locations</h2>
            <p className="section-note">Active locations appear in the Scheduling Grid. Toggle to deactivate without deleting.</p>

            <div className="add-item-row">
              <input
                type="text"
                placeholder="New location name..."
                value={newLocationName}
                onChange={(e) => setNewLocationName(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleAddLocation()}
              />
              <button onClick={handleAddLocation} className="btn btn-primary">Add Location</button>
            </div>

            {locationLoading ? (
              <div className="loading">Loading...</div>
            ) : (
              <ul className="manage-list">
                {locations.length === 0 && (
                  <li className="manage-empty">No locations yet. Add one above.</li>
                )}
                {[...locations].sort((a, b) => {
                    if (b.active !== a.active) return b.active ? 1 : -1;
                    return a.name.localeCompare(b.name);
                  }).map(loc => (
                  <li key={loc.id} className={`manage-item ${!loc.active ? 'inactive' : ''}`}>
                    {editingLocationId === loc.id ? (
                      <>
                        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '6px' }}>
                          <input
                            className="item-edit-input"
                            value={editingLocationName}
                            onChange={e => setEditingLocationName(e.target.value)}
                            onKeyDown={e => { if (e.key === 'Escape') handleCancelEditLocation(); }}
                            placeholder="Location name"
                            autoFocus
                          />
                          <input
                            className="item-edit-input"
                            value={editingLocationAddress}
                            onChange={e => setEditingLocationAddress(e.target.value)}
                            onKeyDown={e => { if (e.key === 'Enter') handleSaveLocationName(); if (e.key === 'Escape') handleCancelEditLocation(); }}
                            placeholder="Street address (for map link)"
                          />
                        </div>
                        <div className="item-actions">
                          <button onClick={handleSaveLocationName} className="btn-toggle is-active">Save</button>
                          <button onClick={handleCancelEditLocation} className="btn-toggle">Cancel</button>
                        </div>
                      </>
                    ) : (
                      <>
                        <div className="item-name">
                          <div>{loc.name}</div>
                          {loc.address && <div style={{ fontSize: '12px', color: '#64748b', marginTop: '2px' }}>{loc.address}</div>}
                        </div>
                        <div className="item-actions">
                          <button onClick={() => handleStartEditLocation(loc)} className="btn-toggle">Edit</button>
                          <button onClick={() => handleToggleLocation(loc)} className={`btn-toggle ${loc.active ? 'is-active' : ''}`}>
                            {loc.active ? 'Deactivate' : 'Activate'}
                          </button>
                          <button onClick={() => handleDeleteLocation(loc.id)} className="btn-delete">Delete</button>
                        </div>
                      </>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}

        {/* Congregations Tab */}
        {activeTab === 'congregations' && (
          <div className="management-section">
            <h2>Manage Congregations</h2>
            <p className="section-note">Congregations listed here are stored in Firebase. Keep this list updated so the registration forms are accurate.</p>

            <div className="add-item-row">
              <input
                type="text"
                placeholder="New congregation name..."
                value={newCongregationName}
                onChange={(e) => setNewCongregationName(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleAddCongregation()}
              />
              <button onClick={handleAddCongregation} className="btn btn-primary">Add Congregation</button>
            </div>

            {congregationLoading ? (
              <div className="loading">Loading...</div>
            ) : (
              <ul className="manage-list">
                {congregations.length === 0 && (
                  <li className="manage-empty">No congregations yet. Add one above.</li>
                )}
                {[...congregations].sort((a, b) => a.name.localeCompare(b.name)).map(cong => {
                  const isExpanded = expandedCong === cong.id;
                  const edits = meetingEdits[cong.id] || {};
                  const hasMeetingTimes = cong.midweekMeetingDay || cong.weekendMeetingDay;
                  return (
                    <li key={cong.id} className={`manage-item ${!cong.active ? 'inactive' : ''}`} style={{ flexDirection: 'column', alignItems: 'stretch' }}>
                      <div style={{ display: 'flex', alignItems: 'center' }}>
                        <span className="item-name" style={{ flex: 1 }}>
                          {cong.name}
                          {hasMeetingTimes && (
                            <span style={{ fontSize: 11, color: '#64748b', marginLeft: 8 }}>
                              {cong.midweekMeetingDay && `${cong.midweekMeetingDay} ${formatTime12(cong.midweekMeetingTime)}`}
                              {cong.midweekMeetingDay && cong.weekendMeetingDay && ' · '}
                              {cong.weekendMeetingDay && `${cong.weekendMeetingDay} ${formatTime12(cong.weekendMeetingTime)}`}
                            </span>
                          )}
                        </span>
                        <div className="item-actions">
                          <button
                            onClick={() => toggleCongExpand(cong)}
                            className="btn-toggle"
                            style={{ background: isExpanded ? '#e0f2fe' : '', color: isExpanded ? '#0369a1' : '' }}
                          >
                            {isExpanded ? 'Close' : 'Meeting Times'}
                          </button>
                          <button onClick={() => handleToggleCongregation(cong)} className={`btn-toggle ${cong.active ? 'is-active' : ''}`}>
                            {cong.active ? 'Deactivate' : 'Activate'}
                          </button>
                          <button onClick={() => handleDeleteCongregation(cong.id)} className="btn-delete">Delete</button>
                        </div>
                      </div>

                      {isExpanded && (
                        <div className="cong-meeting-editor">
                          <div className="cong-meeting-row">
                            <strong>Midweek:</strong>
                            <select
                              value={edits.midweekDay || ''}
                              onChange={e => setMeetingEdits(prev => ({ ...prev, [cong.id]: { ...edits, midweekDay: e.target.value } }))}
                            >
                              <option value="">Day…</option>
                              {['Monday','Tuesday','Wednesday','Thursday','Friday'].map(d => <option key={d} value={d}>{d}</option>)}
                            </select>
                            <input
                              type="time"
                              value={edits.midweekTime || ''}
                              onChange={e => setMeetingEdits(prev => ({ ...prev, [cong.id]: { ...edits, midweekTime: e.target.value } }))}
                              placeholder="Time"
                            />
                          </div>
                          <div className="cong-meeting-row">
                            <strong>Weekend:</strong>
                            <select
                              value={edits.weekendDay || ''}
                              onChange={e => setMeetingEdits(prev => ({ ...prev, [cong.id]: { ...edits, weekendDay: e.target.value } }))}
                            >
                              <option value="">Day…</option>
                              {['Saturday','Sunday'].map(d => <option key={d} value={d}>{d}</option>)}
                            </select>
                            <input
                              type="time"
                              value={edits.weekendTime || ''}
                              onChange={e => setMeetingEdits(prev => ({ ...prev, [cong.id]: { ...edits, weekendTime: e.target.value } }))}
                              placeholder="Time"
                            />
                          </div>
                          <button className="btn btn-primary" style={{ marginTop: 8 }} onClick={() => handleSaveMeetingTimes(cong.id)}>
                            Save Meeting Times
                          </button>
                        </div>
                      )}
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        )}
        {/* Shift Times Tab */}
        {activeTab === 'shifttimes' && (
          <div className="management-section">
            <h2>Manage Shift Times</h2>
            <p className="section-note">Active shift times appear in the Scheduling Grid, Check-In, and Day-Of pages.</p>
            <p className="section-note" style={{ color: '#b45309', background: '#fef3c7', border: '1px solid #fde68a', borderRadius: 5, padding: '6px 10px', marginTop: 6 }}>
              ⚠️ Changing shift times affects existing volunteer assignments and availability filters.
            </p>

            {/* Reset + Migrate actions */}
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, margin: '12px 0', alignItems: 'center' }}>
              <button
                className="btn btn-secondary"
                onClick={handleResetShiftTimes}
                disabled={resettingShifts}
              >
                {resettingShifts ? 'Resetting…' : 'Reset to New Defaults (7-10 AM, 10 AM-1 PM, 1-5 PM, 5-8 PM)'}
              </button>
              <button
                className="btn btn-secondary"
                onClick={handleMigrateAvailability}
                disabled={migratingAvail}
              >
                {migratingAvail ? 'Migrating…' : 'Migrate Volunteer Availability to New Times'}
              </button>
            </div>
            {resetShiftResult && (
              <div className={`result-message ${resetShiftResult.success ? 'success' : 'error'}`} style={{ marginBottom: 10 }}>
                {resetShiftResult.success
                  ? `Shift times reset — ${resetShiftResult.count} defaults loaded.`
                  : `Reset failed: ${resetShiftResult.error}`}
              </div>
            )}
            {migrateAvailResult && (
              <div className={`result-message ${migrateAvailResult.success ? 'success' : 'error'}`} style={{ marginBottom: 10 }}>
                {migrateAvailResult.success
                  ? `Migration complete — ${migrateAvailResult.updated} volunteer record(s) updated.`
                  : `Migration failed: ${migrateAvailResult.error}`}
              </div>
            )}

            <div className="add-item-row" style={{ alignItems: 'flex-end', flexWrap: 'wrap', gap: 8 }}>
              <label style={{ display: 'flex', flexDirection: 'column', fontSize: 12, gap: 3 }}>
                Shift name
                <input
                  type="text"
                  placeholder="e.g. 7-9 AM"
                  value={newShiftName}
                  onChange={e => setNewShiftName(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && handleAddShiftTime()}
                  style={{ width: 130 }}
                />
              </label>
              <label style={{ display: 'flex', flexDirection: 'column', fontSize: 12, gap: 3 }}>
                Order #
                <input
                  type="number"
                  min="1"
                  placeholder="auto"
                  value={newShiftOrder}
                  onChange={e => setNewShiftOrder(e.target.value)}
                  style={{ width: 70 }}
                />
              </label>
              <button onClick={handleAddShiftTime} className="btn btn-primary">Add Shift Time</button>
            </div>

            {shiftLoading ? (
              <div className="loading">Loading...</div>
            ) : (
              <ul className="manage-list">
                {shiftTimes.length === 0 && (
                  <li className="manage-empty">No shift times yet. Add one above or use Load Default Data.</li>
                )}
                {shiftTimes.map(st => (
                  <li key={st.id} className={`manage-item ${!st.active ? 'inactive' : ''}`}>
                    <span className="item-name">{st.name}</span>
                    <span style={{ fontSize: 11, color: '#94a3b8', marginLeft: 8 }}>#{st.order}</span>
                    <div className="item-actions">
                      <button
                        onClick={() => handleToggleShiftTime(st)}
                        className={`btn-toggle ${st.active ? 'is-active' : ''}`}
                      >
                        {st.active ? 'Deactivate' : 'Activate'}
                      </button>
                      <button onClick={() => handleDeleteShiftTime(st.id)} className="btn-delete">
                        Delete
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}

        {/* Messages Tab */}
        {activeTab === 'messages' && (
          <div className="management-section">
            <h2>Email &amp; SMS Message Templates</h2>
            <p className="section-note">
              Edit the wording sent to volunteers. Use <code>{'{{firstName}}'}</code> and <code>{'{{link}}'}</code> as
              placeholders in availability messages, and <code>{'{{volunteerName}}'}</code> in confirmation messages.
              Volunteers in congregations with "Spanish" in the name receive the Spanish template automatically.
            </p>

            {!templatesLoaded ? (
              <div className="loading">Loading templates…</div>
            ) : Object.keys(templateEdits).length === 0 ? (
              <div>
                <p className="section-note">No templates found in the database.</p>
                <button className="btn btn-primary" onClick={handleSeedTemplates}>
                  Initialize Default Templates
                </button>
              </div>
            ) : (
              <div className="msg-template-list">
                {[
                  { id: 'availability_en', label: 'Availability Request', lang: 'English' },
                  { id: 'availability_es', label: 'Availability Request', lang: 'Spanish' },
                  { id: 'confirmation_en', label: 'Confirmation Email', lang: 'English' },
                  { id: 'confirmation_es', label: 'Confirmation Email', lang: 'Spanish' },
                ].map(({ id, label, lang }) => {
                  const t = templateEdits[id] || {};
                  const isAvail = id.startsWith('availability');
                  const saving = templateSaving[id];
                  const saved = templateSaved[id];
                  return (
                    <div key={id} className="msg-template-card">
                      <div className="msg-template-card-header">
                        <span className="msg-type-badge">{label}</span>
                        <span className={`msg-lang-badge ${id.endsWith('_es') ? 'es' : 'en'}`}>{lang}</span>
                        <div className="msg-template-actions">
                          <button
                            className="btn btn-secondary"
                            onClick={() => handleResetTemplate(id)}
                            title="Reset to built-in default"
                          >
                            Reset to Default
                          </button>
                          <button
                            className="btn btn-primary"
                            onClick={() => handleSaveTemplate(id)}
                            disabled={saving}
                          >
                            {saving ? 'Saving…' : saved ? 'Saved ✓' : 'Save Changes'}
                          </button>
                        </div>
                      </div>

                      <div className="msg-template-fields">
                        <label>Email Subject</label>
                        <input
                          type="text"
                          value={t.emailSubject || ''}
                          onChange={e => handleTemplateField(id, 'emailSubject', e.target.value)}
                          className="msg-field-input"
                        />

                        {isAvail && (
                          <>
                            <label>SMS Body <span className="msg-hint">(variables: {`{{firstName}}`}, {`{{link}}`})</span></label>
                            <textarea
                              rows={3}
                              value={t.smsBody || ''}
                              onChange={e => handleTemplateField(id, 'smsBody', e.target.value)}
                              className="msg-field-textarea"
                            />
                            <label>Email Body (HTML) <span className="msg-hint">(variables: {`{{firstName}}`}, {`{{link}}`})</span></label>
                            <textarea
                              rows={10}
                              value={t.emailHtml || ''}
                              onChange={e => handleTemplateField(id, 'emailHtml', e.target.value)}
                              className="msg-field-textarea msg-field-code"
                            />
                          </>
                        )}

                        {!isAvail && (
                          <>
                            <div className="msg-header-row">
                              <label style={{ flex: 1 }}>Day Column Header
                                <input type="text" value={t.tableHeaderDay || ''} onChange={e => handleTemplateField(id, 'tableHeaderDay', e.target.value)} className="msg-field-input" />
                              </label>
                              <label style={{ flex: 1 }}>Location Column Header
                                <input type="text" value={t.tableHeaderLocation || ''} onChange={e => handleTemplateField(id, 'tableHeaderLocation', e.target.value)} className="msg-field-input" />
                              </label>
                              <label style={{ flex: 1 }}>Time Column Header
                                <input type="text" value={t.tableHeaderTime || ''} onChange={e => handleTemplateField(id, 'tableHeaderTime', e.target.value)} className="msg-field-input" />
                              </label>
                            </div>
                            <label>Pre-Table Content (HTML) <span className="msg-hint">(variable: {`{{volunteerName}}`})</span></label>
                            <textarea
                              rows={7}
                              value={t.emailHtmlPre || ''}
                              onChange={e => handleTemplateField(id, 'emailHtmlPre', e.target.value)}
                              className="msg-field-textarea msg-field-code"
                            />
                            <label>Post-Table Content (HTML)</label>
                            <textarea
                              rows={10}
                              value={t.emailHtmlPost || ''}
                              onChange={e => handleTemplateField(id, 'emailHtmlPost', e.target.value)}
                              className="msg-field-textarea msg-field-code"
                            />
                          </>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default CSVImport;
